import React from 'react';
import { Group, Text, Paper, Badge, Image, Box, Stack } from '@mantine/core';
import { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import { UseFormReturnType } from '@mantine/form';
import { Expense } from './AddExpenseModal';
import uploadIcon from '../../../assets/icons/upload.svg';
import trashIcon from '../../../assets/icons/trash.svg';

interface ImageUploaderProps {
    getRootProps: (props?: any) => DropzoneRootProps;
    getInputProps: () => DropzoneInputProps;
    previewImages: string[];
    existingImages: string[];
    setPreviewImages: React.Dispatch<React.SetStateAction<string[]>>;
    setExistingImages: React.Dispatch<React.SetStateAction<string[]>>;
    setRemovedImageIds: React.Dispatch<React.SetStateAction<string[]>>;
    expense?: Expense;
    form: UseFormReturnType<any>;
}

export const ImageUploader = ({
    getRootProps,
    getInputProps,
    previewImages,
    existingImages,
    setPreviewImages,
    setExistingImages,
    setRemovedImageIds,
    expense,
    form
}: ImageUploaderProps) => {

    // Remove a preview image
    const removePreviewImage = (index: number) => {
        form.setFieldValue('receipt_images', []);
        URL.revokeObjectURL(previewImages[index]);
        setPreviewImages([]);
    };

    // Remove an existing image
    const removeExistingImage = (index: number, imageId: string) => {
        setExistingImages([]);
        setRemovedImageIds(prev => [...prev, imageId]);
    };

    return (
        <>
            <Box {...getRootProps()}
                style={{
                    border: '2px dashed #e9ecef',
                    borderRadius: '8px',
                    padding: '32px',
                    textAlign: 'center',
                    cursor: 'pointer'
                }}
                onMouseOver={(e: React.MouseEvent<HTMLDivElement>) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={(e: React.MouseEvent<HTMLDivElement>) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = '';
                }}
            >
                <input {...getInputProps()} />
                <Group justify="center" gap="xs">
                    <img src={uploadIcon} alt="Upload" width={24} />
                    <Text>Drop an image or PDF here or click to upload</Text>
                </Group>
                <Text size="xs" c="dimmed" mt={5}>
                    Supported formats: JPG, PNG, HEIC, PDF - Max 10MB
                </Text>
            </Box>

            {existingImages.length > 0 && (
                <Stack gap="xs" mt="md">
                    <Text size="sm" fw={500}>Current Image</Text>
                    <Group>
                        {existingImages.map((url, index) => (
                            <Paper
                                key={`existing-${index}`}
                                withBorder
                                p="xs"
                                style={{ position: 'relative' }}
                            >
                                <Badge
                                    style={{
                                        position: 'absolute',
                                        top: 5,
                                        right: 5,
                                        zIndex: 2,
                                        cursor: 'pointer',
                                    }}
                                    color="red"
                                    onClick={() => removeExistingImage(index, expense?.receipts?.[index].id || '')}
                                >
                                    <img src={trashIcon} alt="Remove" width={10} />
                                </Badge>
                                <Image
                                    src={url}
                                    alt={`Image ${index + 1}`}
                                    height={100}
                                    width={100}
                                    fit="cover"
                                />
                            </Paper>
                        ))}
                    </Group>
                </Stack>
            )}

            {previewImages.length > 0 && (
                <Stack gap="xs" mt="md">
                    <Text size="sm" fw={500}>New Image</Text>
                    <Group>
                        {previewImages.map((url, index) => (
                            <Paper
                                key={`preview-${index}`}
                                withBorder
                                p="xs"
                                style={{ position: 'relative' }}
                            >
                                <Badge
                                    style={{
                                        position: 'absolute',
                                        top: 5,
                                        right: 5,
                                        zIndex: 2,
                                        cursor: 'pointer',
                                    }}
                                    color="red"
                                    onClick={() => removePreviewImage(index)}
                                >
                                    <img src={trashIcon} alt="Remove" width={10} />
                                </Badge>
                                <Image
                                    src={url}
                                    alt={`Image ${index + 1}`}
                                    height={100}
                                    width={100}
                                    fit="cover"
                                />
                            </Paper>
                        ))}
                    </Group>
                </Stack>
            )}
        </>
    );
}; 